import { useMutation, useQueryClient } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { LinkedInProfile } from "common/types";

import type { Company } from "../types";

async function getProfilesRequest(
  identifiers: string[],
): Promise<{ profiles: LinkedInProfile[]; failed: string[] }> {
  return post(`helpers/identify-profiles`, {
    identifiers,
  });
}
async function addAccountsToBlacklistRequest(
  workspaceId: string,
  accounts: LinkedInProfile[],
) {
  await post(`workspaces/${workspaceId}/blacklist`, {
    urns: accounts.map(({ urn }) => urn),
  });
}
async function addCompaniesToBlacklistRequest(
  workspaceId: string,
  companies: Company[],
) {
  await post(`workspaces/${workspaceId}/blacklist-companies`, {
    companies: companies,
  });
}
async function removeCompanyFromBlacklistRequest(
  workspaceId: string,
  companyUrn: string,
) {
  await del(`workspaces/${workspaceId}/blacklist-companies/${companyUrn}`);
}
async function removeAccountFromBlacklistRequest(
  workspaceId: string,
  targetUrn: string,
) {
  await del(`workspaces/${workspaceId}/blacklist/${targetUrn}`);
}

export default function useBlacklistMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { blacklistKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: addToBlacklist, isPending: isAddingToBlacklist } =
    useMutation({
      mutationFn: (
        data: { accounts: LinkedInProfile[] } | { companies: Company[] },
      ) => {
        if ("accounts" in data) {
          return addAccountsToBlacklistRequest(workspaceId, data.accounts);
        } else {
          return addCompaniesToBlacklistRequest(workspaceId, data.companies);
        }
      },
      onSuccess: (_, data) => {
        queryClient.setQueryData<(LinkedInProfile | Company)[]>(
          blacklistKeys.list("accounts" in data ? "accounts" : "companies"),
          (prevQueryData) => {
            const newItems =
              "accounts" in data ? data.accounts : data.companies;
            if (prevQueryData) {
              return [...prevQueryData, ...newItems];
            }
            return newItems;
          },
        );
      },
    });

  const {
    mutateAsync: removeFromBlacklist,
    isPending: isRemovingFromBlacklist,
  } = useMutation({
    mutationFn: (data: { accountUrn: string } | { companyUrn: string }) => {
      if ("accountUrn" in data) {
        return removeAccountFromBlacklistRequest(workspaceId, data.accountUrn);
      } else {
        return removeCompanyFromBlacklistRequest(workspaceId, data.companyUrn);
      }
    },
    onSuccess: (_, data) => {
      queryClient.setQueryData<(LinkedInProfile | Company)[]>(
        blacklistKeys.list("accountUrn" in data ? "accounts" : "companies"),
        (prevQueryData) => {
          if (prevQueryData) {
            return prevQueryData.filter(
              ({ urn }) =>
                urn !==
                ("accountUrn" in data ? data.accountUrn : data.companyUrn),
            );
          }
        },
      );
    },
  });

  const { mutateAsync: getProfiles, isPending: isGettingProfiles } =
    useMutation({
      mutationFn: ({ identifiers }: { identifiers: string[] }) =>
        getProfilesRequest(identifiers),
    });

  return {
    addToBlacklist,
    isAddingToBlacklist,
    removeFromBlacklist,
    isRemovingFromBlacklist,
    getProfiles,
    isGettingProfiles,
  };
}
